import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Zap, Mail, X, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api, PLATFORM_META } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Dashboard() {
    const { user, refresh } = useAuth();
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hideBanner, setHideBanner] = useState(false);
    const [sendingVerify, setSendingVerify] = useState(false);
    const [openingPortal, setOpeningPortal] = useState(false);

    useEffect(() => {
        refresh();
        api.get("/repurpose/history").then(({ data }) => {
            setHistory(data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [refresh]);

    const resendVerify = async () => {
        setSendingVerify(true);
        try {
            const { data } = await api.post("/auth/send-verification");
            if (data.already_verified) toast.success("Already verified");
            else toast.success("Verification email sent");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send email");
        } finally {
            setSendingVerify(false);
        }
    };

    const openPortal = async () => {
        setOpeningPortal(true);
        try {
            const { data } = await api.post("/billing/portal");
            window.location.href = data.url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not open billing portal");
            setOpeningPortal(false);
        }
    };

    const periodEndStr = user?.current_period_end
        ? new Date(user.current_period_end).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
        : null;
    const subBadge = user?.cancel_at_period_end
        ? `Cancels on ${periodEndStr}`
        : user?.subscription_status === "trialing"
          ? `Free trial — renews ${periodEndStr}`
          : user?.subscription_status === "active"
            ? `Renews ${periodEndStr}`
            : user?.subscription_status === "past_due"
              ? "Payment past due"
              : null;

    const pct = user ? Math.min(100, (user.credits_used / user.monthly_limit) * 100) : 0;
    const remaining = user ? Math.max(0, user.monthly_limit - user.credits_used) : 0;

    return (
        <div className="p-6 sm:p-10 max-w-7xl mx-auto" data-testid="dashboard-root">
            {user && !user.email_verified && !hideBanner && (
                <div className="mb-6 border border-white/15 bg-[#0a0a0a] p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="verify-banner">
                    <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-zinc-300" strokeWidth={1.5} />
                        <div>
                            <div className="text-sm text-white">Verify your email to secure your account.</div>
                            <div className="text-xs text-zinc-500">We sent a link to <span className="font-mono-ui text-white">{user.email}</span>.</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button data-testid="verify-resend-btn" onClick={resendVerify} disabled={sendingVerify} size="sm" variant="outline" className="bg-transparent border-white/15 hover:bg-white/5 text-white">
                            {sendingVerify ? "Sending…" : "Resend email"}
                        </Button>
                        <button data-testid="verify-dismiss" onClick={() => setHideBanner(true)} className="text-zinc-500 hover:text-white p-1">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
                <div>
                    <div className="font-mono-ui text-xs tracking-[0.25em] uppercase text-zinc-500 mb-2">// DASHBOARD</div>
                    <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">
                        Hey, {user?.name?.split(" ")[0] || "creator"}.
                    </h1>
                    <p className="mt-2 text-zinc-400">Ready to multiply your content?</p>
                </div>
                <Link to="/app/new">
                    <Button data-testid="dashboard-new-btn" size="lg" className="bg-white text-black hover:bg-zinc-200 font-semibold h-12 px-6">
                        <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.8} />
                        New Repurpose
                    </Button>
                </Link>
            </div>

            {/* Grid */}
            <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-10">
                <div className="p-6 bg-[#050505] md:col-span-2">
                    <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-zinc-500">Monthly usage</div>
                    <div className="mt-4 flex items-baseline gap-3">
                        <div className="font-display font-black text-5xl tracking-tighter" data-testid="dash-credits-used">{user?.credits_used ?? 0}</div>
                        <div className="text-zinc-500 font-mono-ui text-sm">/ {user?.monthly_limit}</div>
                    </div>
                    <Progress value={pct} className="mt-4 h-1 bg-white/10" />
                    <div className="mt-3 text-sm text-zinc-400" data-testid="dash-remaining">
                        {remaining} repurposes left this month on{" "}
                        <span className="font-mono-ui uppercase text-white">{user?.plan}</span> plan.
                    </div>
                </div>
                <div className="p-6 bg-[#050505] flex flex-col justify-between" data-testid="dash-sub-card">
                    {user?.has_stripe_customer ? (
                        <>
                            <div>
                                <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-zinc-500">Subscription</div>
                                <div className="mt-3 font-display font-bold text-xl tracking-tight capitalize">{user.plan} plan</div>
                                {subBadge && (
                                    <p className="mt-2 text-sm text-zinc-400" data-testid="dash-sub-badge">{subBadge}</p>
                                )}
                            </div>
                            <Button variant="outline" data-testid="dash-manage-sub-btn" onClick={openPortal} disabled={openingPortal} className="mt-4 w-full bg-transparent border-white/15 hover:bg-white/5 text-white">
                                {openingPortal ? "Opening…" : <>Manage subscription <Settings className="w-3.5 h-3.5 ml-1.5" /></>}
                            </Button>
                        </>
                    ) : (
                        <>
                            <div>
                                <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-zinc-500">Need more?</div>
                                <div className="mt-3 font-display font-bold text-xl tracking-tight">Upgrade plan</div>
                                <p className="mt-2 text-sm text-zinc-400">7-day free trial. Up to 500 repurposes + 5 platforms.</p>
                            </div>
                            <Link to="/pricing">
                                <Button variant="outline" data-testid="dash-upgrade-btn" className="mt-4 w-full bg-transparent border-white/15 hover:bg-white/5 text-white">
                                    View plans <ArrowUpRight className="w-3.5 h-3.5 ml-1.5" />
                                </Button>
                            </Link>
                        </>
                    )}
                </div>
            </div>

            {/* Recent */}
            <div>
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <div className="font-mono-ui text-xs tracking-[0.25em] uppercase text-zinc-500 mb-1">// RECENT</div>
                        <h2 className="font-display font-bold text-2xl tracking-tight">Latest repurposes</h2>
                    </div>
                    <Link to="/app/history" data-testid="dash-view-all" className="text-sm text-zinc-400 hover:text-white">
                        View all →
                    </Link>
                </div>

                {loading ? (
                    <div className="text-zinc-500 font-mono-ui text-xs uppercase tracking-widest">Loading…</div>
                ) : history.length === 0 ? (
                    <div className="border border-white/10 p-12 text-center">
                        <Zap className="w-8 h-8 mx-auto text-zinc-600 mb-3" strokeWidth={1.5} />
                        <div className="font-display font-bold text-xl">No repurposes yet</div>
                        <p className="text-zinc-500 text-sm mt-1">Start by pasting a YouTube link or some text.</p>
                        <Link to="/app/new">
                            <Button data-testid="dash-empty-cta" className="mt-6 bg-white text-black hover:bg-zinc-200">
                                Create your first
                            </Button>
                        </Link>
                    </div>
                ) : (
                    <div className="border border-white/10">
                        {history.slice(0, 5).map((h, i) => (
                            <Link
                                key={h.id}
                                to={`/app/result/${h.id}`}
                                data-testid={`dash-item-${i}`}
                                className="flex items-center justify-between gap-6 p-5 hover:bg-white/5 transition-colors border-b border-white/10 last:border-b-0 group"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-zinc-500">
                                        {new Date(h.created_at).toLocaleDateString()} — {h.source_type}
                                    </div>
                                    <div className="mt-1 font-display font-semibold truncate">
                                        {h.source_title || (h.source_type === "youtube" ? h.source : h.source.slice(0, 80))}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    {h.outputs.slice(0, 5).map((o) => (
                                        <span key={o.platform} className="font-mono-ui text-[10px] uppercase tracking-widest text-zinc-400 px-2 py-1 border border-white/10">
                                            {PLATFORM_META[o.platform]?.label || o.platform}
                                        </span>
                                    ))}
                                </div>
                                <ArrowUpRight className="w-4 h-4 text-zinc-600 group-hover:text-white transition-colors" />
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
